import { apiService } from "./api.service";

const ETL_URL = import.meta.env.VITE_API_URL_ETL || "https://worker-etl.betalog-ventas.workers.dev";

async function request<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const fullUrl = `${ETL_URL}${path}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string> | undefined),
  };
  const token = apiService.getToken();
  const sessionId = apiService.getSessionId();
  const csrfToken = apiService.getCsrfToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (sessionId) headers["X-Session-Id"] = sessionId;
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;

  const res = await fetch(fullUrl, { ...options, headers, credentials: "include" });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err: any = new Error(json.error || "Error en la petición");
    err.status = res.status;
    err.data = json;
    throw err;
  }
  return json.data as T;
}

export interface BuildingParameters {
  width: number;
  depth: number;
  height: number;
  floors: number;
  roofType: "flat" | "pitched" | "green";
  wallMaterial: "brick" | "concrete" | "wood" | "steel";
  windowCount: number;
  doorCount: number;
  finishQuality: "basic" | "standard" | "premium" | "luxury";
}

export interface Model3DGeometry {
  version: 1;
  dimensions: { width: number; depth: number; height: number; totalHeight: number; floors: number };
  floor: { width: number; depth: number; y: number };
  walls: Array<{
    id: string;
    x: number; y: number; z: number;
    width: number; height: number; depth: number;
    orientation: string;
  }>;
  roof: { type: string; width: number; depth: number; y: number; height: number };
  windows: Array<{ id: string; x: number; y: number; z: number; width: number; height: number; wall: string }>;
  doors: Array<{ id: string; x: number; y: number; z: number; width: number; height: number; wall: string }>;
  materials: {
    wall: string;
    floor: string;
    roof: string;
    window: string;
    door: string;
  };
}

export interface ClientModel3D {
  id: string;
  clientId: string;
  name: string;
  description: string | null;
  format: string;
  status: "draft" | "ready_for_quote" | "quoted";
  parameters: BuildingParameters | null;
  geometry: Model3DGeometry | null;
  tokensCharged: number;
  generationInput: string | null;
  parserUsed: string | null;
  readyForQuoteAt: number | null;
  quoteRequestedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface ClientModelListItem {
  id: string;
  clientId: string;
  name: string;
  description: string | null;
  status: "draft" | "ready_for_quote" | "quoted";
  tokensCharged: number;
  generationInput: string | null;
  parserUsed: string | null;
  readyForQuoteAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export const clientModelsService = {
  generate(text: string, name?: string) {
    return request<{
      model: ClientModel3D;
      transactionId: string;
      parsed: { parameters: BuildingParameters; confidence: number; parserUsed: string };
    }>("/api/client/models/generate", {
      method: "POST",
      body: JSON.stringify({ text, name }),
    });
  },
  list() {
    return request<ClientModelListItem[]>("/api/client/models");
  },
  get(id: string) {
    return request<ClientModel3D>(`/api/client/models/${id}`);
  },
  update(id: string, data: { name?: string; description?: string; parameters?: BuildingParameters }) {
    return request<ClientModel3D>(`/api/client/models/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
  markReady(id: string) {
    return request<ClientModel3D>(`/api/client/models/${id}/ready`, { method: "POST" });
  },
  remove(id: string) {
    return request<void>(`/api/client/models/${id}`, { method: "DELETE" });
  },
};